/**
 * The pure half of the `/data` table's layout: which columns open collapsed
 * under "Hide additional" (desktop-one-ground, 2026-10-24), and how a saved
 * column order meets the columns a page actually has (members-filters,
 * 2026-10-26). Kept out of `pages/Data.tsx` so the SPA's DOM-less test runner
 * can pin it (`data-table-defaults.test.ts`).
 */

/** The two fields of a `DEFAULT_LAYOUT` entry that decide the answer. */
export interface AdditionalDefaults {
  /** Columns named as additional. */
  readonly additional?: ReadonlyArray<string>;
  /**
   * The ONLY columns that open visible. Every other header opens additional —
   * one the entity names in its order and equally one the server adds later,
   * which a hand-kept `additional` list would leave showing.
   */
  readonly primary?: ReadonlyArray<string>;
}

/**
 * The columns an entity opens with marked additional: its named `additional`
 * list, plus — when it declares `primary` — every header outside it, each
 * once, the named ones first.
 */
export function defaultAdditionalColumns(
  layout: AdditionalDefaults | undefined,
  headers: ReadonlyArray<string>,
): string[] {
  const named = layout?.additional ?? [];
  const primary = layout?.primary;
  if (primary === undefined) return [...named];
  return [...new Set([...named, ...headers.filter((h) => !primary.includes(h))])];
}

/**
 * The saved column order reconciled with the columns a page has: vanished
 * columns dropped, new ones appended in header order. Returns `prev` itself
 * when nothing changes, so the caller's state setter can bail out.
 *
 * A page with no rows (a search or filter that matched nobody) has no header
 * set at all. It says nothing about which columns exist, so the order stays
 * as saved: reconciling against it would empty the order, and the table
 * would persist that and lose the staffer's arrangement (members-filters,
 * 2026-10-26).
 */
export function reconcileColumnOrder(
  prev: string[],
  headers: ReadonlyArray<string>,
): string[] {
  if (headers.length === 0) return prev;
  const existing = prev.filter((h) => headers.includes(h));
  const added = headers.filter((h) => !existing.includes(h));
  const merged = [...existing, ...added];
  const unchanged =
    merged.length === prev.length && merged.every((h, i) => h === prev[i]);
  return unchanged ? prev : merged;
}
